import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
// import { Link } from 'react-router-dom'; // Sign In — enable when auth is wired up
import { X, ChevronDown } from 'lucide-react';
import { VEDA_ONLINE_DOCS_URL } from '../../constants/countryDashboard';

function GlobalHeaderMobileMenu({
  isOpen,
  onClose,
  vedaOnlineUrl,
  explainersHref,
  githubLinks,
  showUnderstandingEnergyProfiles,
  onUnderstandingEnergyProfilesClick,
}) {
  const [githubExpanded, setGithubExpanded] = useState(false);

  const handleOverlayClick = useCallback(() => {
    onClose();
  }, [onClose]);

  const handlePanelClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const handleUnderstandingClick = useCallback(() => {
    onUnderstandingEnergyProfilesClick?.();
    onClose();
  }, [onUnderstandingEnergyProfilesClick, onClose]);

  const handleMenuLinkClick = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleGithubToggle = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setGithubExpanded((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setGithubExpanded(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const menuLinkClass =
    'block w-full text-left px-5 py-3.5 text-sm font-medium text-gray-800 hover:bg-gray-50 border-b border-gray-200';
  const githubSubLinkClass =
    'block w-full text-left pl-8 pr-5 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 border-b border-gray-200';

  return createPortal(
    <div
      className="fixed inset-0 z-[11000] flex items-center justify-center bg-black/60 px-4"
      role="presentation"
      onClick={handleOverlayClick}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="VERVESTACKS menu"
        className="relative w-full max-w-sm bg-white rounded-2xl shadow-xl flex flex-col overflow-hidden"
        onClick={handlePanelClick}
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <span className="text-vervestacks-primary font-bold text-lg tracking-wide">VERVESTACKS</span>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-full p-1.5 text-vervestacks-primary hover:bg-gray-100"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex flex-col">
          <a
            href={vedaOnlineUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={menuLinkClass}
            onClick={handleMenuLinkClick}
          >
            Veda Online
          </a>
          <a
            href={VEDA_ONLINE_DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={menuLinkClass}
            onClick={handleMenuLinkClick}
          >
            Documentation
          </a>
          <a href={explainersHref} className={menuLinkClass} onClick={handleMenuLinkClick}>
            Explainers
          </a>
          {githubLinks.type === 'multiple' ? (
            <div className="border-b border-gray-200">
              <button
                type="button"
                onClick={handleGithubToggle}
                aria-expanded={githubExpanded}
                className="flex w-full items-center justify-between px-5 py-3.5 text-sm font-medium text-gray-800 hover:bg-gray-50 text-left"
              >
                <span>GitHub</span>
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-gray-500 transition-transform duration-200 ${githubExpanded ? 'rotate-180' : ''}`}
                  aria-hidden
                />
              </button>
              {githubExpanded &&
                githubLinks.items.map((item) => (
                  <a
                    key={item.url}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={githubSubLinkClass}
                    onClick={handleMenuLinkClick}
                  >
                    {item.name || 'GitHub'}
                  </a>
                ))}
            </div>
          ) : (
            <a
              href={githubLinks.url}
              target="_blank"
              rel="noopener noreferrer"
              className={menuLinkClass}
              onClick={handleMenuLinkClick}
            >
              GitHub
            </a>
          )}
          {showUnderstandingEnergyProfiles && onUnderstandingEnergyProfilesClick && (
            <button
              type="button"
              className={`${menuLinkClass} border-b-0`}
              onClick={handleUnderstandingClick}
            >
              Understanding Energy Profiles
            </button>
          )}
        </nav>

        {/* Sign In — re-enable when authentication is ready
        <div className="px-5 py-5 mt-1">
          <Link
            to="/login"
            className="btn-primary block w-full text-center py-3"
            onClick={handleMenuLinkClick}
          >
            Sign In
          </Link>
        </div>
        */}
      </div>
    </div>,
    document.body
  );
}

export default React.memo(GlobalHeaderMobileMenu);
